import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../Loaders/Loader";
import ContentApprovalReviewLink from "./ContentApprovalReviewLink";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ModalBasicLayout from "../Modal/ModalBasicLayout";
import Button from "../Buttons/Button";
import ContentApprovalPreview from "./ContentApprovalPreview";
import ContentApprovalCommentList from "./ContentApprovalCommentList";
import ContentApprovalStatusChip from "./ContentApprovalStatusChip";
import ApprovalReviewersStatus from "./ApprovalReviewersStatus";
import ContentApprovalCreativeCards from "../../pages/ContentApproval/components/ContentApprovalCreativeCards";
import { useTranslationApp } from "../../lib/i18next";
import {
  CONTENT_APPROVAL_EDIT_BUTTON,
  CONTENT_APPROVAL_REPLACE_BUTTON,
  CONTENT_APPROVAL_SAVE_REVISION_BUTTON,
  CONTENT_APPROVAL_CLIENT_COMMENTS_TITLE,
  CONTENT_APPROVAL_CLIENT_COMMENTS_EMPTY,
  CONTENT_APPROVAL_STATUS_SECTION_LABEL,
} from "../../i18n/keysTranslations";
import {
  contentApprovalLayoutRootSx,
  contentApprovalMainSx,
  contentApprovalSidebarSx,
  CONTENT_APPROVAL_SIDEBAR_WIDTH,
  CONTENT_APPROVAL_WORKSPACE_BG,
} from "./contentApprovalLayout";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { getExtensionFile } from "../../utils/string";
import { VIDEO_FORMATS } from "../../utils/constants";
import { getCarouselUrls, isCarouselItem } from "../../utils/carouselUtils";
import {
  buildContentApprovalPayloadFromGalleryItem,
  filterActiveContentApprovalComments,
  getContentApprovalCurrentDraftIDsByContentID,
  getContentApprovalStateForContent,
  getReviewersWithStatusForContent,
} from "./contentApprovalUtils";
import {
  deriveApprovalStatusFromReviewers,
  hasMultipleApprovalReviewers,
} from "./approvalReviewersUtils";

const getBaseCarouselUrls = (item, pendingMedia) => {
  if (pendingMedia?.urls?.length) {
    return [...pendingMedia.urls];
  }
  return getCarouselUrls(item);
};

const buildPreviewItem = (activeItem, pendingMedia) => {
  if (!activeItem) {
    return null;
  }

  if (!pendingMedia) {
    return activeItem;
  }

  if (pendingMedia.urls?.length) {
    const carouselImages = pendingMedia.urls.map((url, index) => ({
      url,
      id: `${activeItem.id}-pending-${index}`,
    }));
    return {
      ...activeItem,
      isCarousel: true,
      carouselImages,
      url: carouselImages[0]?.url,
      type: "carousel",
    };
  }

  return {
    ...activeItem,
    url: pendingMedia.url,
    isCarousel: false,
    carouselImages: null,
    type: pendingMedia.type || activeItem.type,
  };
};

const ContentApprovalReviewModal = ({
  modalOpen = false,
  onCloseModal = () => {},
  item = null,
  batchItems = [],
  approvalData = null,
  comments = [],
  isLoading = false,
  isSaving = false,
  onCopyLink = () => {},
  onReplace = () => {},
  onEdit = () => {},
  onSave = () => {},
}) => {
  const { t } = useTranslationApp();
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [pendingMediaByContentID, setPendingMediaByContentID] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedContentIDRef = useRef(null);

  const reviewItems = useMemo(() => {
    if (Array.isArray(batchItems) && batchItems.length > 0) {
      return batchItems;
    }
    return item ? [item] : [];
  }, [batchItems, item]);

  const payloads = useMemo(
    () =>
      reviewItems.map((reviewItem) =>
        buildContentApprovalPayloadFromGalleryItem(reviewItem, approvalData)
      ),
    [approvalData, reviewItems]
  );

  const currentDraftIDsByContentID = useMemo(
    () =>
      getContentApprovalCurrentDraftIDsByContentID(reviewItems, approvalData),
    [approvalData, reviewItems]
  );

  const activeItem = reviewItems[selectedIndex] || item;
  const activePendingMedia = activeItem?.id
    ? pendingMediaByContentID[activeItem.id] || null
    : null;

  const activeApprovalState = useMemo(() => {
    return getContentApprovalStateForContent({
      approvalData,
      contentID: activeItem?.id,
      fallbackStatus: approvalData
        ? null
        : activeItem?.approvalStatus || activeItem?.status || null,
      fallbackCurrentDraftID: approvalData
        ? null
        : activeItem?.currentDraftID || null,
      fallbackDraftRevision: approvalData ? 0 : activeItem?.draftRevision || 0,
    });
  }, [activeItem, approvalData]);

  useEffect(() => {
    if (!modalOpen) {
      setPendingMediaByContentID({});
      setCarouselIndex(0);
      selectedContentIDRef.current = null;
      return;
    }

    if (!item?.id || reviewItems.length === 0) {
      return;
    }

    const targetContentID = selectedContentIDRef.current || item.id;
    const index = reviewItems.findIndex(
      (entry) => entry.id === targetContentID
    );
    setSelectedIndex(index >= 0 ? index : 0);
  }, [modalOpen, item?.id, reviewItems]);

  useEffect(() => {
    selectedContentIDRef.current = activeItem?.id || null;
  }, [activeItem?.id]);

  useEffect(() => {
    setCarouselIndex(0);
  }, [activeItem?.id]);

  const setPendingMediaForContent = (contentID, pendingMedia) => {
    if (!contentID) {
      return;
    }

    setPendingMediaByContentID((prev) => {
      if (!pendingMedia) {
        if (!prev[contentID]) {
          return prev;
        }
        const next = { ...prev };
        delete next[contentID];
        return next;
      }

      return {
        ...prev,
        [contentID]: pendingMedia,
      };
    });
  };

  const isReviewingCarousel = useMemo(
    () =>
      Boolean(activePendingMedia?.urls?.length) ||
      isCarouselItem(activeItem) ||
      (activeItem?.isCarousel === true &&
        Array.isArray(activeItem?.carouselImages) &&
        activeItem.carouselImages.length > 0),
    [activeItem, activePendingMedia]
  );

  const previewItem = useMemo(
    () => buildPreviewItem(activeItem, activePendingMedia),
    [activeItem, activePendingMedia]
  );

  const isCarousel = isCarouselItem(previewItem);
  const carouselImages = isCarousel ? previewItem?.carouselImages || [] : [];
  const currentUrl = isCarousel
    ? carouselImages[carouselIndex]?.url || previewItem?.url
    : previewItem?.url;

  const currentCarouselImage = isCarousel
    ? carouselImages[carouselIndex] || null
    : null;
  const mediaWidth = currentCarouselImage?.width ?? previewItem?.width ?? null;
  const mediaHeight =
    currentCarouselImage?.height ?? previewItem?.height ?? null;

  const extension = getExtensionFile(currentUrl || "");
  const isVideo = VIDEO_FORMATS.includes(extension);

  const activeComments = useMemo(
    () =>
      filterActiveContentApprovalComments(comments, {
        contentID: activeItem?.id || "",
        currentDraftID: activeApprovalState.currentDraftID || "",
        currentDraftIDsByContentID,
        types: null,
      }),
    [
      activeApprovalState.currentDraftID,
      activeItem?.id,
      comments,
      currentDraftIDsByContentID,
    ]
  );
  const hasPendingChanges = Boolean(activePendingMedia);
  const approvalUrl = approvalData?.url || "";
  const isMultiReviewer = hasMultipleApprovalReviewers(approvalData?.reviewers);
  const reviewersWithStatus = useMemo(
    () =>
      getReviewersWithStatusForContent({
        approvalData,
        contentID: activeItem?.id,
      }),
    [activeItem?.id, approvalData]
  );
  const approvalStatus = deriveApprovalStatusFromReviewers({
    reviewers: reviewersWithStatus,
    fallbackStatus: activeApprovalState.status || null,
  });

  const onHandleReplace = () => {
    const contentID = activeItem?.id;
    if (!contentID) {
      return;
    }

    onReplace({
      onSelected: (selectedItem) => {
        if (!selectedItem) {
          return;
        }

        const replacementUrl =
          selectedItem.isCarousel &&
          Array.isArray(selectedItem.carouselImages) &&
          selectedItem.carouselImages.length > 0
            ? selectedItem.carouselImages[0]?.url
            : selectedItem.url;

        if (!replacementUrl) {
          return;
        }

        if (isReviewingCarousel) {
          const baseUrls = getBaseCarouselUrls(activeItem, activePendingMedia);
          if (!baseUrls.length) {
            return;
          }

          const nextUrls = [...baseUrls];
          nextUrls[carouselIndex] = replacementUrl;
          setPendingMediaForContent(contentID, {
            urls: nextUrls,
            type: "carousel",
          });
          return;
        }

        if (
          selectedItem.isCarousel &&
          Array.isArray(selectedItem.carouselImages) &&
          selectedItem.carouselImages.length > 0
        ) {
          setPendingMediaForContent(contentID, {
            urls: selectedItem.carouselImages.map((img) => img.url),
            type: "carousel",
          });
          setCarouselIndex(0);
          return;
        }

        const selectedExtension = getExtensionFile(replacementUrl);
        setPendingMediaForContent(contentID, {
          url: replacementUrl,
          type: VIDEO_FORMATS.includes(selectedExtension) ? "video" : "image",
        });
        setCarouselIndex(0);
      },
    });
  };

  const onHandleEdit = () => {
    const contentID = activeItem?.id;
    const sourceItem = activeItem;
    if (!contentID || !sourceItem) {
      return;
    }

    const editingCarousel = isReviewingCarousel;
    const editingCarouselIndex = carouselIndex;

    onEdit({
      contentID,
      url: currentUrl,
      carouselIndex: editingCarouselIndex,
      isCarousel: editingCarousel,
      onSaved: (editedUrl) => {
        if (!editedUrl) {
          return;
        }

        if (editingCarousel) {
          setPendingMediaByContentID((prev) => {
            const currentPending = prev[contentID] || null;
            const baseUrls = getBaseCarouselUrls(sourceItem, currentPending);
            if (!baseUrls.length) {
              return prev;
            }

            const nextUrls = [...baseUrls];
            nextUrls[editingCarouselIndex] = editedUrl;
            return {
              ...prev,
              [contentID]: { urls: nextUrls, type: "carousel" },
            };
          });
          return;
        }

        setPendingMediaForContent(contentID, {
          url: editedUrl,
          type: "image",
        });
      },
    });
  };

  const onHandleSave = async () => {
    const contentID = activeItem?.id;
    if (!contentID || !activePendingMedia) {
      return;
    }

    const pendingChange = {
      contentID,
      ...activePendingMedia,
    };

    if (!pendingChange.url && !pendingChange.urls?.length) {
      return;
    }

    const result = await onSave([pendingChange]);
    if (!result?.ok) {
      return;
    }

    if ((result.savedContentIDs || []).includes(contentID)) {
      setPendingMediaForContent(contentID, null);
    }
  };

  if (!activeItem) {
    return null;
  }

  const onSelectCreative = (index) => {
    setSelectedIndex(index);
    selectedContentIDRef.current = reviewItems[index]?.id || null;
    setCarouselIndex(0);
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth
      fullScreen
      hasTitle={false}
      sxPaper={{
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        maxHeight: "100%",
        m: 0,
        borderRadius: 0,
      }}
      sxContent={{
        p: 0,
        px: 0,
        pt: 0,
        pb: 0,
        m: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        bgcolor: CONTENT_APPROVAL_WORKSPACE_BG,
      }}
    >
      <IconButton
        onClick={onCloseModal}
        aria-label="close"
        sx={{
          position: "absolute",
          top: 12,
          right: { xs: 12, md: CONTENT_APPROVAL_SIDEBAR_WIDTH + 12 },
          zIndex: 2,
          bgcolor: "background.paper",
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          "&:hover": {
            bgcolor: "background.paper",
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      {isLoading || !approvalData ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 240,
          }}
        >
          <Loader />
        </Box>
      ) : (
        <Box sx={contentApprovalLayoutRootSx}>
          <Box
            sx={{
              ...contentApprovalMainSx,
              overflowY: "auto",
              p: { xs: 2, md: 3 },
            }}
          >
            <ContentApprovalCreativeCards
              payloads={payloads}
              selectedIndex={selectedIndex}
              onSelect={onSelectCreative}
            />
            <ContentApprovalPreview
              currentUrl={currentUrl}
              isCarousel={isCarousel && carouselImages.length > 1}
              carouselIndex={carouselIndex}
              carouselCount={carouselImages.length}
              onPrevCarousel={() =>
                setCarouselIndex((prev) => Math.max(0, prev - 1))
              }
              onNextCarousel={() =>
                setCarouselIndex((prev) =>
                  Math.min(carouselImages.length - 1, prev + 1)
                )
              }
              showVersionBadge={false}
              showCardFrame={false}
              mediaWidth={mediaWidth}
              mediaHeight={mediaHeight}
            />
          </Box>

          <Box sx={contentApprovalSidebarSx}>
            <Box
              sx={{
                p: 2.5,
                borderBottom: `1px solid ${INPUT_COLOR_BORDER}`,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {approvalStatus ? (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      letterSpacing: "0.5px",
                      color: "text.secondary",
                      fontSize: "0.65rem",
                    }}
                  >
                    {t(CONTENT_APPROVAL_STATUS_SECTION_LABEL)}
                  </Typography>
                  <ContentApprovalStatusChip status={approvalStatus} />
                </Box>
              ) : null}

              <ApprovalReviewersStatus reviewers={reviewersWithStatus} />

              {approvalUrl && !isMultiReviewer ? (
                <ContentApprovalReviewLink
                  approvalUrl={approvalUrl}
                  onCopied={onCopyLink}
                />
              ) : null}
            </Box>

            <Box
              sx={{
                px: 2.5,
                pt: 2,
                pb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ChatBubbleOutlineIcon
                sx={{ fontSize: 18, color: "text.secondary" }}
              />
              <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
                {t(CONTENT_APPROVAL_CLIENT_COMMENTS_TITLE)}
              </Typography>
              {activeComments.length > 0 && (
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: "#111",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#fff", fontWeight: 700, fontSize: "0.65rem" }}
                  >
                    {activeComments.length}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                px: 2.5,
                pb: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ContentApprovalCommentList
                comments={activeComments}
                showCreativeLabel={false}
                centeredEmpty
                emptyText={t(CONTENT_APPROVAL_CLIENT_COMMENTS_EMPTY)}
              />
            </Box>

            <Box
              sx={{
                p: 2.5,
                borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  type="button"
                  color="secondary"
                  onClick={onHandleReplace}
                  sx={{ flex: 1 }}
                >
                  {t(CONTENT_APPROVAL_REPLACE_BUTTON)}
                </Button>
                {!isVideo ? (
                  <Button
                    type="button"
                    color="secondary"
                    onClick={onHandleEdit}
                    sx={{ flex: 1 }}
                  >
                    {t(CONTENT_APPROVAL_EDIT_BUTTON)}
                  </Button>
                ) : null}
              </Box>
              <Button
                type="button"
                fullWidth
                disabled={!hasPendingChanges || isSaving}
                loading={isSaving}
                onClick={onHandleSave}
              >
                {t(CONTENT_APPROVAL_SAVE_REVISION_BUTTON)}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </ModalBasicLayout>
  );
};

export default ContentApprovalReviewModal;
